import React from "react";
import * as Sentry from "@sentry/react";

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  eventId: string | null;
}

const containerStyles: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  backgroundColor: "var(--v2-app-bg)",
  padding: "24px",
};

const cardStyles: React.CSSProperties = {
  backgroundColor: "var(--v2-surface-bg)",
  borderRadius: "8px",
  padding: "48px",
  maxWidth: "600px",
  width: "100%",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  textAlign: "center",
};

const headingStyles: React.CSSProperties = {
  color: "var(--v2-error-text)",
  fontSize: "24px",
  fontWeight: 600,
  marginBottom: "16px",
  marginTop: 0,
};

const paragraphStyles: React.CSSProperties = {
  color: "var(--v2-error-text)",
  opacity: 0.8,
  fontSize: "14px",
  lineHeight: "1.6",
  marginBottom: "24px",
};

const buttonStyles: React.CSSProperties = {
  backgroundColor: "#07988f",
  color: "#ffffff",
  border: "none",
  borderRadius: "4px",
  padding: "10px 24px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  marginBottom: "16px",
};

const copyButtonStyles: React.CSSProperties = {
  backgroundColor: "transparent",
  color: "#07988f",
  border: "1px solid #07988f",
  borderRadius: "4px",
  padding: "4px 12px",
  fontSize: "12px",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const eventIdContainerStyles: React.CSSProperties = {
  marginTop: "24px",
  padding: "16px",
  backgroundColor: "var(--v2-surface-bg-muted)",
  borderRadius: "4px",
  border: "1px solid var(--v2-border-subtle)",
};

const eventIdLabelStyles: React.CSSProperties = {
  color: "var(--v2-error-text)",
  opacity: 0.65,
  fontSize: "12px",
  marginBottom: "8px",
};

const eventIdBoxStyles: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
};

const eventIdTextStyles: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: "13px",
  color: "var(--v2-error-text)",
  wordBreak: "break-all",
  flex: 1,
};

// CSS for hover effects (applied via style tag in the component)
const hoverStyles = `
  .error-boundary-try-again-btn:hover {
    background-color: #069386 !important;
  }
  .error-boundary-copy-btn:hover {
    background-color: #07988f !important;
    color: #ffffff !important;
  }
`;

class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true, eventId: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const eventId = Sentry.captureException(error, { 
      extra: { componentStack: errorInfo.componentStack } 
    });
    this.setState({ eventId: eventId || null });
    console.error("Unhandled error captured by AppErrorBoundary", error, errorInfo);
  }

  handleReset = () => {
    if (typeof window !== "undefined" && window.location) {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false, eventId: null });
  };

  handleCopyEventId = () => {
    if (this.state.eventId) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.state.eventId).catch((err) => {
          console.error("Failed to copy event ID:", err);
        });
      } else {
        // Fallback for environments without clipboard API
        console.warn("Clipboard API not available");
      }
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" style={containerStyles}>
          <style>{hoverStyles}</style>
          <div style={cardStyles}>
            <h3 style={headingStyles}>Something went wrong.</h3>
            <p style={paragraphStyles}>
              Our team has been notified. Please refresh the page or try again in a
              few moments.
            </p>
            <button
              onClick={this.handleReset}
              aria-label="try-again"
              style={buttonStyles}
              className="error-boundary-try-again-btn"
            >
              Try again
            </button>
            {this.state.eventId && (
              <div style={eventIdContainerStyles}>
                <div style={eventIdLabelStyles}>Error ID (for support)</div>
                <div style={eventIdBoxStyles}>
                  <div style={eventIdTextStyles}>{this.state.eventId}</div>
                  <button
                    onClick={this.handleCopyEventId}
                    style={copyButtonStyles}
                    aria-label="copy-event-id"
                    className="error-boundary-copy-btn"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppErrorBoundaryWithSentry = ({ children }: AppErrorBoundaryProps) => (
  <Sentry.ErrorBoundary fallback={<></>} onError={(error, componentStack) => {
    console.error("Sentry error boundary caught", error, componentStack);
  }}>
    <AppErrorBoundary>{children}</AppErrorBoundary>
  </Sentry.ErrorBoundary>
);

export default AppErrorBoundaryWithSentry;
export { AppErrorBoundary };
